/**
 * @fileoverview Web transport implementation for the differential client Store.
 *
 * Implements the Transport interface using Reboot's StoreV2 web client RPCs,
 * handling proto serialization/deserialization at the boundary.
 *
 * This is the browser-side counterpart of RebootTransport, using WebContext
 * instead of ExternalContext.
 *
 * @module RebootWebTransport
 */
import type { WebContext } from "@reboot-dev/reboot-web";
import type { ChangeEntry, RawOperation, Row, Transport } from "differential/src/client/Transport";

import type {
	ChangeEntry as ChangeEntryProto,
	Operation as OperationProto,
	StoreV2WeakReference,
} from "../../dist/dev/moment/differential/v1alpha2/store_rbt_web";
import { operationToProto } from "./RebootTransport";

/**
 * Web transport for the differential client Store.
 *
 * Wraps a StoreV2 web WeakReference and WebContext, implementing the
 * context-free Transport interface by capturing context at construction time.
 */
export class RebootWebTransport implements Transport {
	private readonly storeRef: StoreV2WeakReference;
	private readonly context: WebContext;

	constructor(storeRef: StoreV2WeakReference, context: WebContext) {
		this.storeRef = storeRef;
		this.context = context;
	}

	async mutate(
		lastSeenVersion: number,
		operations: RawOperation[]
	): Promise<{ version: number; changes: ChangeEntry[] }> {
		const protoOps = operations.map((op) => operationToProto(op));
		const result = await this.storeRef.mutate(this.context, {
			lastSeenVersion: BigInt(lastSeenVersion),
			operations: protoOps,
		});
		const changes: ChangeEntry[] = result.changes.map(protoChangeToEntry);
		return { version: Number(result.version), changes };
	}

	async state(): Promise<{ version: number; tables: Record<string, Row[]> }> {
		const result = await this.storeRef.snapshot(this.context, {});
		const tables: Record<string, Row[]> = {};
		for (const [table, tableRows] of Object.entries(result.tables)) {
			tables[table] = tableRows.rows.map((s) => s.toJson() as Row);
		}
		return { version: Number(result.version), tables };
	}

	async changes(sinceVersion: number): Promise<{ version: number; changes: ChangeEntry[] }> {
		const result = await this.storeRef.changes(this.context, {
			sinceVersion: BigInt(sinceVersion),
		});
		const changes: ChangeEntry[] = result.changes.map(protoChangeToEntry);
		return { version: Number(result.version), changes };
	}
}

function protoChangeToEntry(proto: ChangeEntryProto): ChangeEntry {
	return {
		version: Number(proto.version),
		operation: protoOperationToRaw(proto.operation!),
	};
}

function protoOperationToRaw(proto: OperationProto): RawOperation {
	switch (proto.op.case) {
		case "insert":
			return {
				type: "INSERT",
				table: proto.op.value.table,
				value: proto.op.value.value ? (proto.op.value.value.toJson() as Row) : {},
			};
		case "delete":
			return {
				type: "DELETE",
				table: proto.op.value.table,
				value: proto.op.value.value ? (proto.op.value.value.toJson() as Row) : {},
			};
		case "replace":
			return {
				type: "REPLACE",
				table: proto.op.value.table,
				value: proto.op.value.value ? (proto.op.value.value.toJson() as Row) : {},
			};
		case "upsert":
			return {
				type: "UPSERT",
				table: proto.op.value.table,
				value: proto.op.value.value ? (proto.op.value.value.toJson() as Row) : {},
			};
		case "createTable":
			return {
				type: "CREATE_TABLE",
				table: proto.op.value.table,
				primaryKey: proto.op.value.primaryKey,
			};
		case "dropTable":
			return {
				type: "DROP_TABLE",
				table: proto.op.value.table,
			};
		case undefined:
			throw new Error("Operation has no op set");
	}
}
